import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Phone, MessageCircle, CheckCircle2, Star, ShieldCheck, Award, BadgeCheck,
  Car, MapPin, Fuel, Mountain, Users, Clock, ChevronLeft, ChevronRight, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import {
  COMPANY,
  SELF_DRIVE_CARS,
  KINNAUR_SPITI_TAXI_PACKAGES,
  KINNAUR_SPITI_TAXI_INCLUSIONS,
  KINNAUR_SPITI_ATTRACTIONS,
  KINNAUR_SPITI_TAXI_FAQS,
  KINNAUR_SPITI_TAXI_PICKUPS,
  KINNAUR_SPITI_TAXI_REVIEWS,
  saveEnquiry,
} from '../mock';

const CAT_META = {
  '4x4': { label: 'Adventure & 4×4', emoji: '🏔' },
  suv: { label: 'SUVs', emoji: '🚘' },
  sedan: { label: 'Hatchbacks & Sedans', emoji: '🚙' },
  luxury: { label: 'Luxury Cars', emoji: '💎' },
};
const CAT_ORDER = ['4x4', 'suv', 'sedan', 'luxury'];

const PACKAGE_FEATURES = [
  'Private Cab Included',
  'Fuel, Tolls & Parking',
  'Experienced Mountain Driver',
  'Inner Line Permit Support',
];

const WA_BASE = `https://wa.me/${COMPANY.whatsapp}`;
const waLink = (msg) => `${WA_BASE}?text=${encodeURIComponent(msg)}`;
const HERO_IMG = '/images/kinnaur-spiti-cab/hero-taxi.jpg';

export default function KinnaurSpitiCabPage() {
  return (
    <div className="pb-20 md:pb-0">
      <Helmet>
        <title>Kinnaur Spiti Taxi Packages & Cab Rentals | From Delhi, Chandigarh, Shimla & Manali</title>
        <meta
          name="description"
          content="Best taxi packages for Kinnaur Spiti Valley — private cabs, SUVs, Innova Crysta, Thar & luxury vehicles. Delhi to Spiti, Chandigarh to Kaza, Manali to Spiti taxi. Starting ₹13,000/person. EMI available."
        />
        <meta property="og:title" content="Kinnaur Spiti Taxi Packages & Cab Rentals | From Delhi, Chandigarh, Shimla & Manali" />
        <meta
          property="og:description"
          content="Best taxi packages for Kinnaur Spiti Valley — private cabs, SUVs, Innova Crysta, Thar & luxury vehicles. Starting ₹13,000/person. EMI available."
        />
        <meta property="og:image" content={HERO_IMG} />
      </Helmet>

      <PageSubNav />
      <HeroWithQuote />
      <TrustStrip />
      <PackagesSection />
      <FleetSection />
      <InclusionsSection />
      <AttractionsSection />
      <ReviewsSection />
      <FAQSection />
      <FinalCTA />
      <MobileStickyBar />
    </div>
  );
}

/* ─── Sticky in-page nav (Packages / Fleet / Destinations / FAQ) ─── */
function PageSubNav() {
  const links = [
    { href: '#packages', label: 'Packages' },
    { href: '#fleet', label: 'Fleet' },
    { href: '#attractions', label: 'Destinations' },
    { href: '#faq', label: 'FAQ' },
  ];
  return (
    <div className="sticky top-[68px] md:top-[78px] z-30 bg-white/95 backdrop-blur-md border-b border-brand-greyDark shadow-sm">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 h-12 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="px-3 py-1.5 rounded-lg text-[13px] font-semibold text-brand-blue/75 hover:text-brand-orange hover:bg-brand-orange/5 whitespace-nowrap transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
        <a href={`tel:${COMPANY.phone}`} className="shrink-0">
          <Button size="sm" className="bg-brand-orange hover:bg-brand-orangeDark text-white h-8 px-3 rounded-lg text-[12.5px] font-semibold">
            <Phone size={13} className="mr-1.5" /> Call Now
          </Button>
        </a>
      </div>
    </div>
  );
}

/* ─── Hero + integrated quote form (matches Lovable layout) ─── */
function HeroWithQuote() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Toyota Innova Crysta and Mahindra Thar taxi convoy on Spiti Valley mountain road — Kinnaur Spiti taxi package"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/95 via-brand-blue/88 to-brand-blue/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-transparent to-brand-blue/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-12 pb-16 lg:pt-16 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Left copy */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full text-[12px] font-semibold mb-5">
              <BadgeCheck size={13} className="text-brand-orange" />
              Himachal Govt. Registered · Since 2010
            </div>

            <h1 className="font-poppins font-extrabold text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.08] tracking-tight">
              Kinnaur Spiti Taxi Packages &amp;{' '}
              <span className="text-brand-orange">Cab Rentals</span>
            </h1>

            <p className="mt-4 text-white/85 text-[15px] lg:text-[16.5px] font-medium leading-relaxed max-w-xl">
              Private taxis, SUVs, Innova Crysta, Thar &amp; luxury cabs for the Spiti Valley road trip.
              Pickup from <strong className="text-white">Delhi, Chandigarh, Shimla, Manali, Kalka</strong> &amp; Kaza.
              Custom itineraries for families, couples, friends &amp; adventure groups.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="bg-white/10 border border-white/15 rounded-2xl px-5 py-3">
                <div className="text-white/65 text-[11px] font-medium uppercase tracking-wider">Starting From</div>
                <div className="font-poppins font-extrabold text-[26px] text-brand-orange leading-none mt-0.5">
                  ₹13,000<span className="text-[14px] font-semibold text-white/80">/ person</span>
                </div>
              </div>
              <div className="bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 font-semibold text-[13px] px-4 py-3 rounded-2xl">
                EMI Available
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${COMPANY.phone}`}>
                <Button className="bg-white text-brand-blue hover:bg-white/90 h-11 px-5 rounded-xl font-semibold">
                  <Phone size={16} className="mr-2" /> Call Now
                </Button>
              </a>
              <a
                href={waLink(
                  "Hi Mountains Chain Travel! 👋\n\nI'm interested in Kinnaur Spiti taxi packages.\n\nPlease share available cab packages, vehicle options & pricing.\n\nThanks!"
                )}
                target="_blank"
                rel="noreferrer"
              >
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-5 rounded-xl font-semibold">
                  <MessageCircle size={16} className="mr-2" /> WhatsApp Now
                </Button>
              </a>
              <a href="#quote">
                <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-5 rounded-xl font-semibold btn-glow">
                  Get Free Quote
                </Button>
              </a>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-2 max-w-lg">
              {[
                { icon: BadgeCheck, t: 'Himachal Govt. Registered' },
                { icon: Award, t: '15+ Years Experience' },
                { icon: Users, t: '10 Lakh+ Happy Travelers' },
                { icon: Star, t: '4.9★ Rated' },
              ].map((x, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/8 px-3 py-2 rounded-lg text-[12px] font-semibold">
                  <x.icon size={14} className="text-brand-orange shrink-0" />
                  {x.t}
                </div>
              ))}
            </div>
          </div>

          {/* Right quote form */}
          <QuoteFormCard />
        </div>
      </div>
    </section>
  );
}

function QuoteFormCard() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    pickup: '',
    travelDates: '',
    travellers: '',
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup) {
      toast({ title: 'Please fill required fields', description: 'Name, phone & pickup city are required.' });
      return;
    }
    await saveEnquiry({
      name: form.name,
      phone: form.phone,
      pickup: form.pickup,
      travelDates: form.travelDates,
      travellers: form.travellers,
      type: 'kinnaur-spiti-taxi',
      destination: 'Kinnaur Spiti',
    });
    toast({
      title: '✨ Quote request received!',
      description: 'Our team will WhatsApp you within 5 minutes with the best cab quote.',
    });
    setForm({ name: '', phone: '', pickup: '', travelDates: '', travellers: '' });
  };

  return (
    <div id="quote" className="bg-white rounded-3xl shadow-premium border border-white/20 p-6 sm:p-7 relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-orange/10" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-poppins font-bold text-brand-blue text-[20px] sm:text-[22px] leading-tight">
            Get Your Free Taxi Quote
          </h2>
          <span className="shrink-0 bg-brand-orange/10 text-brand-orange text-[11px] font-bold px-2.5 py-1 rounded-full">
            ⚡ 5-min reply
          </span>
        </div>
        <p className="text-brand-blue/60 text-[13px] mb-5">Best-priced private cab quote on WhatsApp.</p>

        <form onSubmit={submit} className="space-y-3">
          <Input
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Your Name"
            className="h-11 rounded-xl"
            required
          />
          <Input
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="Phone / WhatsApp"
            className="h-11 rounded-xl"
            required
          />
          <select
            value={form.pickup}
            onChange={(e) => update('pickup', e.target.value)}
            className="w-full h-11 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
            required
          >
            <option value="" disabled>
              Pickup City
            </option>
            {KINNAUR_SPITI_TAXI_PICKUPS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <Input
              value={form.travelDates}
              onChange={(e) => update('travelDates', e.target.value)}
              placeholder="Travel Dates"
              className="h-11 rounded-xl"
            />
            <Input
              type="number"
              min="1"
              value={form.travellers}
              onChange={(e) => update('travellers', e.target.value)}
              placeholder="Travellers"
              className="h-11 rounded-xl"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-12 rounded-xl font-semibold btn-glow mt-1"
          >
            <Car size={17} className="mr-2" />
            Book Taxi — Get Instant Quote
          </Button>
          <p className="text-center text-brand-blue/45 text-[11px] leading-relaxed">
            By submitting, you agree to be contacted about your Kinnaur Spiti taxi booking.
          </p>
        </form>
      </div>
    </div>
  );
}

function TrustStrip() {
  const items = [
    { emoji: '🏔', t: '15+ Years in Himalayan Travel' },
    { emoji: '🚙', t: '200+ Vehicle Fleet' },
    { emoji: '🛡', t: 'Govt. Registered Operator' },
    { emoji: '⭐', t: '4.9 / 5 · 10 Lakh+ Travelers' },
  ];
  return (
    <section className="bg-white border-y border-brand-greyDark">
      <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((s, i) => (
          <div key={i} className="flex items-center gap-2.5 justify-center lg:justify-start text-center lg:text-left">
            <span className="text-[20px]">{s.emoji}</span>
            <div className="font-poppins font-semibold text-brand-blue text-[13px] leading-snug">{s.t}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Packages carousel (like Lovable ‹ ›) ─── */
function PackagesSection() {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('article');
    const amount = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section id="packages" className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div className="max-w-2xl">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">
            Taxi Packages
          </div>
          <h2 className="font-poppins text-[28px] lg:text-[40px] text-brand-blue leading-[1.1]">
            Kinnaur Spiti Taxi Packages &amp; <span className="text-brand-orange">Private Cab Tours</span>
          </h2>
          <p className="text-brand-blue/60 text-[14.5px] mt-3 leading-relaxed">
            Fixed-price, all-inclusive private cab packages for Kinnaur Spiti Valley — from short weekend runs
            to the full 10-day Lahaul-Spiti circuit.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Previous packages"
            onClick={() => scrollBy(-1)}
            className="w-10 h-10 rounded-full border border-brand-greyDark bg-white text-brand-blue hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Next packages"
            onClick={() => scrollBy(1)}
            className="w-10 h-10 rounded-full border border-brand-greyDark bg-white text-brand-blue hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors flex items-center justify-center"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth no-scrollbar"
        style={{ scrollbarWidth: 'none' }}
      >
        {KINNAUR_SPITI_TAXI_PACKAGES.map((pkg) => (
          <article
            key={pkg.id}
            className={`relative snap-start shrink-0 w-[300px] sm:w-[320px] bg-white rounded-2xl overflow-hidden border shadow-card card-hover flex flex-col ${
              pkg.popular ? 'border-brand-orange ring-1 ring-brand-orange/30' : 'border-brand-greyDark'
            }`}
          >
            {pkg.popular && (
              <div className="absolute top-3 right-3 z-10 bg-brand-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
                ★ Most Popular
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="bg-brand-blue/8 text-brand-blue text-[11px] font-semibold px-2.5 py-1 rounded-full">
                  {pkg.badge}
                </span>
                <span className="bg-brand-orange/10 text-brand-orange text-[11px] font-semibold px-2.5 py-1 rounded-full">
                  {pkg.nights} Nights / {pkg.days} Days
                </span>
              </div>
              <h3 className="font-poppins font-semibold text-brand-blue text-[16px] leading-snug">{pkg.title}</h3>
              <p className="text-brand-blue/55 text-[12.5px] mt-2 leading-relaxed flex items-start gap-1.5">
                <MapPin size={13} className="text-brand-orange mt-0.5 shrink-0" />
                {pkg.route}
              </p>
              <ul className="mt-3 space-y-1.5 flex-1">
                {PACKAGE_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-brand-blue/75 text-[12.5px]">
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="pt-4 flex items-end justify-between gap-3 border-t border-brand-grey mt-4">
                <div>
                  <div className="text-brand-blue/50 text-[11px] font-medium">Starts From</div>
                  <div className="font-poppins font-extrabold text-brand-orange text-[22px] leading-none">
                    ₹{pkg.price.toLocaleString('en-IN')}
                  </div>
                  <div className="text-emerald-600 text-[11px] font-semibold mt-0.5">EMI Available</div>
                </div>
                <a
                  href={waLink(
                    `Hi Mountains Chain Travel! 👋\n\nI'd like to book this Kinnaur Spiti taxi package:\n\n*${pkg.title}*\n${pkg.nights}N/${pkg.days}D · From ₹${pkg.price.toLocaleString('en-IN')}\nRoute: ${pkg.route}\n\nPlease share availability & final quote. Thanks!`
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-10 px-4 rounded-xl font-semibold text-[13px]">
                    Book Taxi
                  </Button>
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ─── Fleet: category cards with price list + car images ─── */
function FleetSection() {
  return (
    <section id="fleet" className="bg-brand-grey py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">
            Our Taxi Fleet
          </div>
          <h2 className="font-poppins text-[28px] lg:text-[40px] text-brand-blue leading-[1.1]">
            Cars, SUVs &amp; 4×4 Vehicles for <span className="text-brand-orange">Spiti Valley Road Trip</span>
          </h2>
          <p className="text-brand-blue/60 text-[14px] mt-3 max-w-2xl mx-auto">
            Choose from adventure 4×4s, family SUVs, sedans and luxury cars — every vehicle is mountain-serviced
            and driven by a Himalayan expert.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {CAT_ORDER.map((cat) => {
            const cars = SELF_DRIVE_CARS.filter((c) => c.cat === cat);
            const meta = CAT_META[cat];
            return (
              <div
                key={cat}
                className="bg-white rounded-2xl border border-brand-greyDark shadow-card p-5 sm:p-6 flex flex-col"
              >
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="text-[22px]">{meta.emoji}</span>
                  <h3 className="font-poppins font-semibold text-brand-blue text-[18px]">{meta.label}</h3>
                </div>

                <div className="space-y-0 divide-y divide-brand-grey flex-1">
                  {cars.map((car) => (
                    <div key={car.name} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-14 h-10 rounded-lg overflow-hidden bg-brand-grey shrink-0 border border-brand-greyDark">
                          <img src={car.image} alt={car.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="font-medium text-brand-blue text-[13.5px] truncate">{car.name}</div>
                      </div>
                      <div className="text-brand-orange font-bold text-[12.5px] whitespace-nowrap shrink-0">
                        {car.price.replace('/day', ' / day').replace('–', ' – ')}
                      </div>
                    </div>
                  ))}
                </div>

                <a
                  href={waLink(
                    `Hi Mountains Chain Travel! 👋\n\nI'd like to enquire about ${meta.label} vehicles for a Kinnaur Spiti taxi tour.\n\nPlease share rates & availability.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex text-brand-orange text-[13.5px] font-semibold hover:underline"
                >
                  Enquire for {meta.label} →
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function InclusionsSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">
          What&apos;s Included
        </div>
        <h2 className="font-poppins text-[28px] lg:text-[38px] text-brand-blue leading-[1.1]">
          Everything Included in Your <span className="text-brand-orange">Kinnaur Spiti Taxi Package</span>
        </h2>
        <p className="text-brand-blue/60 text-[14px] mt-3 max-w-2xl mx-auto">
          No hidden charges. No per-km surprises. One fixed price for your entire Spiti Valley car package.
        </p>
      </div>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
        {KINNAUR_SPITI_TAXI_INCLUSIONS.map((item) => (
          <li
            key={item}
            className="flex items-center gap-3 bg-white border border-brand-greyDark rounded-xl px-4 py-3.5 shadow-card"
          >
            <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 text-[13px] font-bold">
              ✓
            </span>
            <span className="text-brand-blue/85 text-[14px] font-medium">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AttractionsSection() {
  return (
    <section id="attractions" className="bg-brand-grey py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">
            Top Attractions
          </div>
          <h2 className="font-poppins text-[28px] lg:text-[38px] text-brand-blue leading-[1.1]">
            Where Your Kinnaur Spiti Taxi <span className="text-brand-orange">Will Take You</span>
          </h2>
          <p className="text-brand-blue/60 text-[14px] mt-3 max-w-2xl mx-auto">
            From Chitkul — the last village of India — to the sacred Chandratal Lake and the mighty Kunzum Pass.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {KINNAUR_SPITI_ATTRACTIONS.map((a) => (
            <div
              key={a.name}
              className="relative rounded-2xl overflow-hidden aspect-[4/3] img-zoom-wrap group shadow-card"
            >
              <img src={a.image} alt={a.name} className="w-full h-full object-cover img-zoom" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/90 via-brand-blue/20 to-transparent" />
              <h3 className="absolute bottom-4 left-4 right-4 font-poppins font-semibold text-white text-[16px] leading-snug">
                {a.name}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewsSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">
          Traveler Reviews
        </div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">
          Trusted by Road Trippers <span className="text-brand-orange">Across India</span>
        </h2>
        <p className="text-brand-blue/60 text-[14px] mt-2">
          Real reviews from families, couples and adventure groups who booked our Kinnaur Spiti taxi service.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {KINNAUR_SPITI_TAXI_REVIEWS.map((r, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-brand-greyDark shadow-card">
            <div className="flex text-brand-orange text-[16px]" aria-label="5 stars">
              ★★★★★
            </div>
            <p className="text-brand-blue/85 text-[13.5px] mt-3 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
            <div className="mt-4 pt-4 border-t border-brand-grey text-[13px] font-semibold text-brand-blue">
              — {r.name}
              {r.city ? `, ${r.city}` : ''}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">FAQ</div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">
          Kinnaur Spiti Taxi — <span className="text-brand-orange">Frequently Asked Questions</span>
        </h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {KINNAUR_SPITI_TAXI_FAQS.map((f, i) => (
          <AccordionItem key={i} value={`ks-${i}`} className="border-brand-greyDark">
            <AccordionTrigger className="text-left text-brand-blue font-semibold text-[15px] hover:text-brand-orange hover:no-underline py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-brand-blue/70 text-[14.5px] leading-relaxed pb-5">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="max-w-7xl mx-auto px-6 mb-8">
      <div className="relative overflow-hidden rounded-3xl bg-brand-blue p-10 lg:p-14 text-center">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_IMG}')` }}
        />
        <div className="absolute inset-0 bg-brand-blue/85" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">
            <Clock size={14} /> Same-Day Quotes
          </div>
          <h2 className="font-poppins font-bold text-white text-[28px] lg:text-[40px] leading-[1.1]">
            Ready for the Spiti Road Trip?
          </h2>
          <p className="text-white/75 text-[15px] mt-3 max-w-2xl mx-auto leading-relaxed">
            Get a same-day, all-inclusive taxi quote — Delhi to Spiti, Chandigarh to Kaza, Shimla to Spiti,
            Manali to Spiti or a custom Kinnaur circuit.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <a href={`tel:${COMPANY.phone}`}>
              <Button className="bg-white text-brand-blue hover:bg-white/90 h-12 px-6 rounded-xl font-semibold">
                <Phone size={17} className="mr-2" /> Call Now
              </Button>
            </a>
            <a
              href={waLink(
                "Hi Mountains Chain Travel! 👋\n\nI'm ready to book a Kinnaur Spiti taxi for my road trip.\n\nPlease share packages & pricing.\n\nThanks!"
              )}
              target="_blank"
              rel="noreferrer"
            >
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6 rounded-xl font-semibold">
                <MessageCircle size={17} className="mr-2" /> WhatsApp Now
              </Button>
            </a>
            <a href="#quote">
              <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-6 rounded-xl font-semibold btn-glow">
                Get Free Quote
              </Button>
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-white/50 text-[12px]">
            <span className="flex items-center gap-1.5">
              <Fuel size={13} /> Fuel included
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={13} /> Permit support
            </span>
            <span className="flex items-center gap-1.5">
              <Mountain size={13} /> Expert mountain drivers
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Mobile sticky Call / WhatsApp / Email (like Lovable footer bar) ─── */
function MobileStickyBar() {
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-brand-greyDark shadow-[0_-4px_20px_rgba(0,0,0,0.08)] safe-area-pb">
      <div className="grid grid-cols-3 divide-x divide-brand-greyDark">
        <a
          href={`tel:${COMPANY.phone}`}
          className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-brand-blue hover:bg-brand-grey transition-colors"
        >
          <Phone size={18} className="text-brand-orange" />
          <span className="text-[11px] font-semibold">Call</span>
        </a>
        <a
          href={waLink(
            "Hi Mountains Chain Travel! 👋\n\nI'm interested in Kinnaur Spiti taxi packages.\n\nPlease share pricing. Thanks!"
          )}
          target="_blank"
          rel="noreferrer"
          className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-brand-blue hover:bg-brand-grey transition-colors"
        >
          <MessageCircle size={18} className="text-emerald-600" />
          <span className="text-[11px] font-semibold">WhatsApp</span>
        </a>
        <a
          href={`mailto:${COMPANY.email}?subject=${encodeURIComponent('Kinnaur Spiti Taxi Quote')}`}
          className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-brand-blue hover:bg-brand-grey transition-colors"
        >
          <Mail size={18} className="text-brand-orange" />
          <span className="text-[11px] font-semibold">Email</span>
        </a>
      </div>
    </div>
  );
}
